import Keycloak from 'keycloak-js'

import { config } from '@/core/config'
import Identity from '@/helpers/identity'
import store from '@/store'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useKeycloakService } from '@/services/keycloak.service'
import { reportStartupIssue } from '@/core/logging/sentry'
import { readSessionItem, removeSessionItem } from '@/helpers/safeStorage'

const sessionStorageStartAuthDateKey = 'start-auth-date'

function getNowTimeStamp() {
  return Math.floor(Date.now() / 1000)
}

async function storeToken() {
  if (window.keycloak && window.keycloak.tokenParsed) {
    const token = window.keycloak.tokenParsed
    await store.dispatch('auth/setToken', token)
    useAuthStore().setToken(token)

    if (token && token.rl_profile) {
      useUserStore().setUpFromRlProfile(token.rl_profile)
    }
  }
}

export async function createKeycloak() {
  let keycloak = new Keycloak(`https://${location.hostname}/keycloak-phoenix.json`)
  let keycloakInitOptions = {
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    redirectUri: window.location.href,
    // keycloak-js commence par verifier les cookies tiers dans une iframe cachee pointant vers
    // auth.horiz.io. Dans un navigateur integre cette iframe ne repond jamais, et la promesse
    // est REJETEE au bout du delai. Le delai d'origine est de 10 s : c'est 10 s d'ecran vide
    // avant que le visiteur puisse continuer. On le raccourcit, l'iframe repondant en general
    // en quelques centaines de millisecondes quand elle le peut.
    messageReceiveTimeout: 5000,
  }

  const refreshAccessTokenCallback = async () => {
    await useKeycloakService().getToken()
  }

  // Refresh Token
  keycloak.onTokenExpired = () => {
    keycloak
      .updateToken(config.keycloak.tokenTtl)
      .then(() => {})
      .catch(function () {})
      .then(() => {})
      .catch(function () {})
  }
  keycloak.onAuthSuccess = async () => {
    await storeToken()
    useAuthStore().setIsLoggedIn()
  }
  keycloak.onAuthLogout = () => {
    window.removeEventListener('focus', refreshAccessTokenCallback)
    useAuthStore().$reset()
  }
  keycloak.onAuthRefreshSuccess = async () => {
    await storeToken()
  }
  const startAuthDateKey = readSessionItem(sessionStorageStartAuthDateKey)
  let userSignInLessThant5min = false

  if (startAuthDateKey) {
    userSignInLessThant5min = getNowTimeStamp() - parseInt(startAuthDateKey) < 60 * 5
  }
  removeSessionItem(sessionStorageStartAuthDateKey)

  // L'objet est publie AVANT l'initialisation. S'il manquait, la garde du routeur
  // (src/router/middleware/requiredAuth.js) ne pouvait plus renvoyer le visiteur vers
  // l'inscription : elle appelle keycloak.login(), qui a besoin de cet objet.
  // eslint-disable-next-line no-undef
  window.keycloak = keycloak

  let authenticated = false

  try {
    authenticated = await keycloak.init(keycloakInitOptions)
  } catch (e) {
    // Echec d'initialisation : ce n'est PAS une raison d'empecher la visite. Un visiteur sans
    // compte n'a aucun besoin de Keycloak pour atteindre l'inscription ou le paiement, et la
    // garde du routeur sait deja traiter le cas non authentifie. Un client deja connecte sera
    // simplement renvoye vers auth.horiz.io, qui le reconnaitra et le ramenera aussitot.
    reportStartupIssue(e, 'keycloak-init')

    return
  }

  if (authenticated) {
    if (userSignInLessThant5min) {
      await store.dispatch('auth/setUserSignInLessThant5min')
    }
    await storeToken()
    useAuthStore().setIsLoggedIn()
    window.identity = new Identity(keycloak)
    await window.identity.ready
    window.addEventListener('focus', refreshAccessTokenCallback)
  }
}
